import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import BatterieLevelComponent from '@/components/batterielevel/batterielevel.component.vue';
import { weatherService } from '@/services/';
import { State } from 'vuex-class';
import { ForecastResult, WeatherResult } from '@/services/weather/weather.interfaces';
import { Cache } from 'aws-amplify';
import { addHours } from 'date-fns';
import { Storage } from 'aws-amplify';
import { Logger } from 'aws-amplify';
import * as Plotly from 'plotly.js-dist';
import { Layout } from 'plotly.js';
const logger = new Logger('Data');
import { fetchWeatherApi } from 'openmeteo';

@Component({
  components: {
    BatterieLevelComponent,
  },
})
export default class Data extends Vue {
  @Prop({ type: Object, required: true }) private readonly camera!: Camera;
  @Prop({ type: Object, required: true }) private project!: Project;
  @State('locale', { namespace: 'profile' }) private readonly locale!: string;

  private visible = false;
  private isDownloadingPDF = false;

  private weather: WeatherResult | null = null;
  private forecast: ForecastResult | null = null;
  private showBatteryGraph: boolean | null = true;
  private openMeteoData: any = null;
  private weatherDisruptions: {
    day: string;
    rain: number;
    wind: number;
    freeze: number;
  }[] = [];
  private monthlyDisruptions: { month: string; rain: number; wind: number; freeze: number }[] = [];

  private get isDeleted(): boolean {
    return (
      this.project.previousCameras !== undefined &&
      this.project.previousCameras.map((c) => c.id).indexOf(this.camera.id) !== -1
    );
  }

  private get temperatureUnit(): 'metric' | 'imperial' {
    return this.locale === 'en' ? 'imperial' : 'metric';
  }

  private get weatherOfDayPicto(): string {
    return this.getPictoOf(this.weather);
  }

  private toggleBatVide = false;
  private interval: NodeJS.Timeout | null = null;
  private get batteriePicto(): string {
    const base = 'picto-erige ';
    if (this.camera.shadow === undefined) {
      return `${base}`;
    } else if (this.camera.shadow.reported.Power_supply === '1') {
      return `${base} picto-batterie-charge`;
    } else if (this.camera.shadow.reported.Battery_Status !== '0') {
      return `${base} picto-batterie-${this.camera.shadow.reported.Battery_Status}`;
    } else {
      if (this.interval) {
        clearInterval(this.interval);
      }
      this.interval = setInterval(() => {
        this.toggleBatVide = !this.toggleBatVide;
      }, 2000);
      return `${base} picto-batterie-0`;
    }
  }

  private handleOpenMeteoResponse(response: any) {
    // Récupération des métadonnées
    const utcOffsetSeconds = response.utcOffsetSeconds();
    const timezone = response.timezone();
    const timezoneAbbreviation = response.timezoneAbbreviation();
    const latitude = response.latitude();
    const longitude = response.longitude();

    // Récupération de l'objet `hourly`
    const hourly = response.hourly()!;

    // Petite helper pour générer les intervalles d'heures
    const range = (start: number, stop: number, step: number) =>
      Array.from({ length: (stop - start) / step }, (_, i) => start + i * step);

    // Construction de notre objet `openMeteoData` pour exploiter les résultats
    this.openMeteoData = {
      hourly: {
        time: range(Number(hourly.time()), Number(hourly.timeEnd()), hourly.interval()).map(
          (t) => new Date((t + utcOffsetSeconds) * 1000)
        ),
        temperature2m: hourly.variables(0)!.valuesArray()!,
        relativeHumidity2m: hourly.variables(1)!.valuesArray()!,
        rain: hourly.variables(2)!.valuesArray()!,
        snowfall: hourly.variables(3)!.valuesArray()!,
        surfacePressure: hourly.variables(4)!.valuesArray()!,
        windSpeed10m: hourly.variables(5)!.valuesArray()!,
      },
      meta: {
        timezone,
        timezoneAbbreviation,
        latitude,
        longitude,
      },
    };
    this.$nextTick(() => {
      this.updatePlotly();
    });
  }

  private updatePlotly() {
    if (!this.openMeteoData) return;

    // 1) Récupération des données
    const times = this.openMeteoData.hourly.time.map((d: Date) => d.toISOString());
    const temp = this.openMeteoData.hourly.temperature2m; // Température
    const humidity = this.openMeteoData.hourly.relativeHumidity2m; // Humidité
    const rain = this.openMeteoData.hourly.rain; // Pluie
    const snow = this.openMeteoData.hourly.snowfall; // Neige
    const pressure = this.openMeteoData.hourly.surfacePressure; // Pression
    const windSpeed = this.openMeteoData.hourly.windSpeed10m; // Vent

    // 2) Traces
    const traceTemp: Partial<Plotly.ScatterData> = {
      x: times,
      y: temp,
      name: 'Température (°C)',
      mode: 'lines',
      type: 'scatter',
      xaxis: 'x',
      yaxis: 'y',
      line: { color: '#FF0000' },
    };

    const traceHum: Partial<Plotly.ScatterData> = {
      x: times,
      y: humidity,
      name: 'Humidité (%)',
      mode: 'lines',
      type: 'scatter',
      xaxis: 'x2',
      yaxis: 'y2',
      line: { color: '#008000' },
    };

    const traceRain: Partial<Plotly.ScatterData> = {
      x: times,
      y: rain,
      name: 'Pluie (mm/h)',
      mode: 'lines',
      type: 'scatter',
      xaxis: 'x3',
      yaxis: 'y3',
      line: { color: '#0000FF' },
    };

    const traceSnow: Partial<Plotly.ScatterData> = {
      x: times,
      y: snow,
      name: 'Neige (mm/h)',
      mode: 'lines',
      type: 'scatter',
      xaxis: 'x4',
      yaxis: 'y4',
      line: { color: '#8B00FF' },
    };

    const tracePressure: Partial<Plotly.ScatterData> = {
      x: times,
      y: pressure,
      name: 'Pression (hPa)',
      mode: 'lines',
      type: 'scatter',
      xaxis: 'x5',
      yaxis: 'y5',
      line: { color: '#FF7F50' },
    };

    const traceWind: Partial<Plotly.ScatterData> = {
      x: times,
      y: windSpeed,
      name: 'Vent (m/s)',
      mode: 'lines',
      type: 'scatter',
      xaxis: 'x6',
      yaxis: 'y6',
      line: { color: '#FFD700' },
    };

    const data = [traceTemp, traceHum, traceRain, traceSnow, tracePressure, traceWind] as Plotly.Data[];

    // 3) Layout
    const layout: Partial<Plotly.Layout> = {
      title: {
        text: 'HISTORIQUE 3 MOIS & PRÉVISION À 7 JOURS',
        font: { 
          color: '#5fc9e4',
          family: 'Poppins, sans-serif', // Utilisation d'une police commune
          size: 17 // Facultatif : Ajuste la taille du texte
        },
      },
      height: 2300,
      grid: {
        rows: 6,
        columns: 1,
        pattern: 'independent',
      },
      margin: {
        l: 70,
        r: 30,
        t: 80,
        b: 80,
      },
      legend: {
        orientation: 'h',
        x: 0,
        y: 1.015,
        font: { color: '#5fc9e4' },
      },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      xaxis: {
        title: {
          text: 'Date/Heure',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      yaxis: {
        title: {
          text: 'Température (°C)',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      xaxis2: {
        title: {
          text: 'Date/Heure',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      yaxis2: {
        title: {
          text: 'Humidité (%)',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      xaxis3: {
        title: {
          text: 'Date/Heure',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      yaxis3: {
        title: {
          text: 'Pluie (mm/h)',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      xaxis4: {
        title: {
          text: 'Date/Heure',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      yaxis4: {
        title: {
          text: 'Neige (mm/h)',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      xaxis5: {
        title: {
          text: 'Date/Heure',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      yaxis5: {
        title: {
          text: 'Pression (hPa)',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      xaxis6: {
        title: {
          text: 'Date/Heure',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
      yaxis6: {
        title: {
          text: 'Vent (m/s)',
          font: { color: '#5fc9e4' },
        },
        tickfont: { color: '#5fc9e4' },
        gridcolor: '#44475a',
        showgrid: true,
      },
    };

    const config = {
      responsive: true,
      scrollZoom: true,
      useResizeHandler: true,
    };

    // 4) Affichage
    Plotly.newPlot(this.$refs.plotlyChart as HTMLDivElement, data, layout, config);
  }

  private async downloadPDFInt() {
    this.isDownloadingPDF = true;
    try {
      // Remplacez 'your-pdf-key.pdf' par la clé de votre PDF dans le bucket S3
      const pdfKey = 'Intemperies.pdf';
      const pdfUrl = await Storage.get(pdfKey, { level: 'public' });

      // Ouvrez le PDF dans un nouvel onglet
      window.open(pdfUrl as string, '_blank');
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error);
      this.$bvToast.toast('Erreur lors du téléchargement du PDF.', {
        title: 'Erreur',
        variant: 'danger',
        solid: true,
      });
    } finally {
      this.isDownloadingPDF = false;
    }
  }

  private async mounted() {
    logger.info('mounted', { camera: this.camera });
    this.onLocaleChange();

    // Votre code existant pour récupérer forecast
    if (this.project.id && this.project.city && this.project.city.latlng) {
      this.forecast = Cache.getItem(`forecast-${this.project.id}`);
      logger.info('get forecast cache', { forecast: this.forecast });
      if (this.forecast === null) {
        weatherService
          .forecastByCoordinate(this.project.city.latlng.lat, this.project.city.latlng.lng)
          .then((forecastResult: ForecastResult) => {
            this.forecast = forecastResult;
            logger.info('set forecast cache', { forecast: this.forecast });
            const expiration = addHours(new Date(), 12);
            Cache.setItem('forecast-' + this.project.id, forecastResult, {
              expires: expiration.getTime(),
            });
          });
      }
    }

    // Appel à Open-Meteo
    if (this.project.city && this.project.city.latlng) {
      const lat = Number(this.project.city.latlng.lat);
      const lng = Number(this.project.city.latlng.lng);

      const params = {
        latitude: lat,
        longitude: lng,
        hourly: ['temperature_2m', 'relative_humidity_2m', 'rain', 'snowfall', 'surface_pressure', 'wind_speed_10m'],
        past_days: 92,
      };
      const url = 'https://api.open-meteo.com/v1/forecast';

      try {
        const responses = await fetchWeatherApi(url, params);
        if (responses && responses.length > 0) {
          this.handleOpenMeteoResponse(responses[0]);
          this.weatherDisruptions = this.calculateWeatherDisruptions();
          this.monthlyDisruptions = this.calculateMonthlyDisruptions();
        }
      } catch (err) {
        console.error('Erreur OpenMeteo:', err);
      }
    }
  }

  private getPictoOf(weather: WeatherResult | null): string {
    const base = 'picto-erige ';
    if (weather === null || weather.weather === undefined || weather.weather[0] === undefined) {
      return base;
    } else {
      switch (weather.weather[0].main) {
        case 'Clear':
          return base + 'picto-meteo-soleil';
        case 'Clouds':
          if (weather.weather[0].icon === '02d' || weather.weather[0].icon === '02n') {
            return base + 'picto-meteo-mixte';
          } else {
            return base + 'picto-meteo-nuage';
          }
        case 'Drizzle':
        case 'Rain':
        case 'Thunderstorm':
        case 'Snow':
        case 'Atmosphere':
        default:
          return base + 'picto-meteo-pluie';
      }
    }
  }

  private calculateWeatherDisruptions() {
    if (!this.openMeteoData) return [];

    const times = this.openMeteoData.hourly.time;
    const temperatures = this.openMeteoData.hourly.temperature2m;
    const rain = this.openMeteoData.hourly.rain;
    const windSpeeds = this.openMeteoData.hourly.windSpeed10m;

    // Jours fériés en France pour les 10 prochaines années
    const holidays = [
      // 2025
      '2025-01-01',
      '2025-04-21',
      '2025-05-01',
      '2025-05-08',
      '2025-05-29',
      '2025-07-14',
      '2025-08-15',
      '2025-11-01',
      '2025-11-11',
      '2025-12-25',
      // 2026
      '2026-01-01',
      '2026-04-06',
      '2026-05-01',
      '2026-05-08',
      '2026-05-21',
      '2026-07-14',
      '2026-08-15',
      '2026-11-01',
      '2026-11-11',
      '2026-12-25',
      // 2027
      '2027-01-01',
      '2027-03-29',
      '2027-05-01',
      '2027-05-08',
      '2027-05-13',
      '2027-07-14',
      '2027-08-15',
      '2027-11-01',
      '2027-11-11',
      '2027-12-25',
      // 2028
      '2028-01-01',
      '2028-04-17',
      '2028-05-01',
      '2028-05-08',
      '2028-05-25',
      '2028-07-14',
      '2028-08-15',
      '2028-11-01',
      '2028-11-11',
      '2028-12-25',
      // 2029
      '2029-01-01',
      '2029-04-02',
      '2029-05-01',
      '2029-05-08',
      '2029-05-10',
      '2029-07-14',
      '2029-08-15',
      '2029-11-01',
      '2029-11-11',
      '2029-12-25',
      // 2030
      '2030-01-01',
      '2030-04-22',
      '2030-05-01',
      '2030-05-08',
      '2030-05-30',
      '2030-07-14',
      '2030-08-15',
      '2030-11-01',
      '2030-11-11',
      '2030-12-25',
      // 2031
      '2031-01-01',
      '2031-04-07',
      '2031-05-01',
      '2031-05-08',
      '2031-05-22',
      '2031-07-14',
      '2031-08-15',
      '2031-11-01',
      '2031-11-11',
      '2031-12-25',
      // 2032
      '2032-01-01',
      '2032-03-29',
      '2032-05-01',
      '2032-05-08',
      '2032-05-13',
      '2032-07-14',
      '2032-08-15',
      '2032-11-01',
      '2032-11-11',
      '2032-12-25',
      // 2033
      '2033-01-01',
      '2033-04-18',
      '2033-05-01',
      '2033-05-08',
      '2033-05-26',
      '2033-07-14',
      '2033-08-15',
      '2033-11-01',
      '2033-11-11',
      '2033-12-25',
      // 2034
      '2034-01-01',
      '2034-04-03',
      '2034-05-01',
      '2034-05-08',
      '2034-05-18',
      '2034-07-14',
      '2034-08-15',
      '2034-11-01',
      '2034-11-11',
      '2034-12-25',
    ];

    const disruptions: { day: string; rain: number; wind: number; freeze: number; total: number }[] = [];

    const dailyData = times.reduce((acc: any, time: Date, index: number) => {
      const day = time.toISOString().split('T')[0];
      const hour = time.getUTCHours();
      const weekday = time.getUTCDay(); // 0 = dimanche, 6 = samedi

      // Exclure les week-ends et jours fériés
      if (weekday === 0 || weekday === 6 || holidays.includes(day)) {
        return acc;
      }

      if (!acc[day]) {
        acc[day] = { rain: { morning: 0, afternoon: 0, total: 0 }, wind: 0, freeze: 0 };
      }

      // Rainfall: Separate morning and afternoon, and round to 2 decimals
      if (hour >= 8 && hour < 13) acc[day].rain.morning += parseFloat((rain[index] || 0).toFixed(2));
      if (hour >= 13 && hour < 18) acc[day].rain.afternoon += parseFloat((rain[index] || 0).toFixed(2));
      acc[day].rain.total += parseFloat((rain[index] || 0).toFixed(2));

      // Wind: Track max wind speed
      acc[day].wind = Math.max(acc[day].wind, windSpeeds[index] || 0);

      // Freeze: Track if temperatures qualify
      if (hour >= 8 && hour < 18) {
        // Heures d'ouverture du chantier
        if (temperatures[index] <= -2) acc[day].freeze = 8;
        // Journée entière
        else if (temperatures[index] > -2 && temperatures[index] < 3) acc[day].freeze = Math.max(acc[day].freeze, 4); // Demi-journée
      }

      return acc;
    }, {});

    Object.entries(dailyData).forEach(([day, data]: any) => {
      const rainDisruption = (() => {
        let hours = 0;

        // Morning rain calculation
        if (data.rain.morning >= 1 && data.rain.morning < 3) hours += Math.min(3, data.rain.morning);
        else if (data.rain.morning >= 3) hours += 5;

        // Afternoon rain calculation
        if (data.rain.afternoon >= 1 && data.rain.afternoon < 3) hours += Math.min(3, data.rain.afternoon);
        else if (data.rain.afternoon >= 3) hours += 5;

        // Total rain > 10 mm
        if (data.rain.total >= 10) hours = 8;

        return hours;
      })();

      const windDisruption = data.wind > 60 ? 3 : 0;

      const totalDisruption = rainDisruption + windDisruption + data.freeze;

      disruptions.push({
        day,
        rain: rainDisruption,
        wind: windDisruption,
        freeze: data.freeze,
        total: totalDisruption,
      });
    });

    return disruptions;
  }

  private onCollapseShown() {
    // Pour le graph météo Plotly
    if (this.$refs.plotlyChart) {
      Plotly.Plots.resize(this.$refs.plotlyChart as HTMLDivElement);
    }

    // Pour le graph batterie
    const batteryGraphComp = this.$refs.batteryGraphRef as any;
    if (batteryGraphComp && batteryGraphComp.resizeBatteryGraph) {
      batteryGraphComp.resizeBatteryGraph();
    }
  }

  private calculateMonthlyDisruptions() {
    if (!this.weatherDisruptions || this.weatherDisruptions.length === 0) return [];

    const monthlyDisruptions = this.weatherDisruptions.reduce((acc: any, disruption) => {
      const month = disruption.day.substring(0, 7); // Format YYYY-MM
      if (!acc[month]) {
        acc[month] = { rain: 0, wind: 0, freeze: 0 };
      }
      acc[month].rain += disruption.rain;
      acc[month].wind += disruption.wind;
      acc[month].freeze += disruption.freeze;
      return acc;
    }, {});

    return Object.entries(monthlyDisruptions).map(([month, values]: any) => ({
      month,
      ...values,
    }));
  }

  @Watch('locale')
  private onLocaleChange() {
    if (this.project.id && this.project.city && this.project.city.latlng) {
      this.weather = Cache.getItem(`weather-${this.project.id}-${this.locale}`);
      if (this.weather === null) {
        weatherService
          .weatherByCoordinate(this.project.city.latlng.lat, this.project.city.latlng.lng, this.temperatureUnit)
          .then((weatherResult) => {
            this.weather = weatherResult;
            const expiration = addHours(new Date(), 12);
            Cache.setItem(`weather-${this.project.id}-${this.locale}`, weatherResult, {
              expires: expiration.getTime(),
            });
          });
      }
    }
  }
}
